<template>
  <div class="fill-height of-y d-flex flex-column">

    <div class="flex-grow-0 pa-2 d-flex align-center flex-wrap" style="gap: 8px">
      <v-autocomplete
          v-model="selectedFarms"
          :items="farms"
          item-text="description"
          item-value="id"
          label="Farms"
          multiple
          small-chips
          deletable-chips
          dense
          hide-details
          style="max-width: 350px"
          no-data-text="No available farms"
      />

      <v-menu
          v-model="startMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
              v-model="startDate"
              label="Start Date"
              prepend-icon="mdi-calendar"
              readonly
              dense
              hide-details
              style="max-width: 170px"
              v-bind="attrs"
              v-on="on"
          />
        </template>
        <v-date-picker v-model="startDate" no-title @input="startMenu = false" />
      </v-menu>

      <v-menu
          v-model="endMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              readonly
              dense
              hide-details
              style="max-width: 170px"
              v-bind="attrs"
              v-on="on"
          />
        </template>
        <v-date-picker v-model="endDate" no-title @input="endMenu = false" />
      </v-menu>

      <v-btn small color="primary" :loading="loading" :disabled="!selectedFarms.length" @click="getData">
        <v-icon left small>mdi-magnify</v-icon>
        Generate
      </v-btn>

      <v-btn
          small
          color="secondary"
          :disabled="!reportData.length"
          :loading="exportingData"
          @click="exportReport"
      >
        <v-icon left small>mdi-microsoft-excel</v-icon>
        Export
      </v-btn>

      <v-spacer />

      <v-text-field
          v-model="search"
          prepend-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          dense
          style="max-width: 220px"
          clearable
      />
    </div>

    <div v-if="loading" class="flex-grow-1 d-flex align-center justify-center">
      <loader />
    </div>

    <div v-else-if="!reportData.length" class="flex-grow-1 d-flex align-center justify-center flex-column">
      <v-icon x-large class="mb-4 grey--text">mdi-bus-multiple</v-icon>
      <span class="grey--text text-h6">Select farms, a date range and click Generate</span>
    </div>

    <div v-else class="flex-grow-1 of-y">
      <v-data-table
          :search="search"
          dense
          fixed-header
          height="100%"
          class="fill-height of-y"
          hide-default-footer
          :headers="headers"
          :items="reportData"
          :options="{ itemsPerPage: reportData.length }"
      >
        <template v-for="col in dateCols" #[`item.${col}`]="{ item }">
          <span :key="col" :class="item[col] ? 'green--text font-weight-bold' : 'grey--text'">{{ item[col] }}</span>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { mapGetters } from 'vuex';
import Loader from '../General/Loader/Loader';

export default {
  components: { Loader },
  props: {
    selectedFarm: Number,
  },
  data() {
    const lastWeekStart = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 6 });
    const lastWeekEnd = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 6 });
    return {
      startDate: format(lastWeekStart, 'yyyy-MM-dd'),
      endDate: format(lastWeekEnd, 'yyyy-MM-dd'),
      startMenu: false,
      endMenu: false,
      reportData: [],
      loading: false,
      exportingData: false,
      search: null,
      selectedFarms: [],
    };
  },
  computed: {
    ...mapGetters(['farms']),
    dateCols() {
      if (!this.reportData.length) return [];
      return Object.keys(this.reportData[0]).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k));
    },
    headers() {
      const fixed = [
        { text: 'Clock No', value: 'clockNo', width: 100 },
        { text: 'Name', value: 'fullName', width: 180 },
        { text: 'Job', value: 'jobDesc', width: 160 },
      ];
      const dateCols = this.dateCols.map(d => ({
        text: d.slice(5),
        value: d,
        width: 60,
        sortable: false,
      }));
      return [
        ...fixed,
        ...dateCols,
        { text: 'Total', value: 'total', width: 70 },
        { text: 'Farms', value: 'farms' },
      ];
    },
  },
  watch: {
    selectedFarm: {
      immediate: true,
      handler(val) {
        if (val && !this.selectedFarms.includes(val))
          this.selectedFarms = [val];
      },
    },
  },
  methods: {
    async getData() {
      try {
        this.loading = true;
        this.reportData = await this.$newReq('POST', 'reports/transport', {
          start: this.startDate,
          end: this.endDate,
          farms: this.selectedFarms,
        });
      } catch (e) {
        console.log(e);
        this.$snack.netWorkError();
      } finally {
        this.loading = false;
      }
    },
    async exportReport() {
      this.exportingData = true;
      try {
        await this.$newReqDownloadFile(
            'POST',
            'reports/transport/export',
            {
              start: this.startDate,
              end: this.endDate,
              farms: this.selectedFarms,
            },
            `Transport Report ${this.startDate} to ${this.endDate}.xlsx`
        );
      } catch (e) {
        console.log(e);
        this.$snack.netWorkError();
      } finally {
        this.exportingData = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
